import 'react-toastify/dist/ReactToastify.css';

import React, { FC, useEffect, useState } from 'react';
import { useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { MainRoutes } from './MainRoutes';
import { getRoute } from './routes';
import { AutoIdleTimer } from './shared/components';
import { LocalStorageEnums } from './shared/enums';
import { ComponentWithGlobalInit } from './shared/hocs';
import { useAuth, useLocalStorage, useMobile } from './shared/hooks';
import { NavigationMenu, SettingsMenu } from './shared/layouts';
import { ConfirmationSnackbarProvider } from './shared/providers';
import { getAppRegionCookie } from './shared/utils/cookies';
import { RootState } from './store/types';
import { hasCriticalError } from './store/user/user.selectors';

const AppContainer: FC = () => {
  const location = useLocation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const [loading, setLoading] = useState<boolean>(false);
  const { initialize, signOut, authenticate, isCheckingAuth } = useAuth();
  const { authenticated, info, settings, preferences, isInMaintenance } = useSelector((state: RootState) => state.user);
  const { data: localizations } = useSelector((state: RootState) => state.localizations);
  const hasCriticalError500 = useSelector(hasCriticalError);
  const { setValue: setIsLegacyToBoltSso } = useLocalStorage<boolean>(LocalStorageEnums.LOGGED_IN_WITH_SSO, false);

  const isLoginPage = location.pathname === getRoute('login');

  // Get the app region and store it in localStorage
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let region = params.get('region');

    // Check if the user logged in via SSO
    const isLegacyToBoltSso = params.get('isLegacyToBoltSso');
    if (isLegacyToBoltSso && isLegacyToBoltSso === 'true') {
      setIsLegacyToBoltSso(true);
    }

    if (!region) {
      region = getAppRegionCookie();
    }

    if (region) {
      localStorage.setItem(LocalStorageEnums.APP_REGION, region);

      // Remove region and isLegacyToBoltSso from URL
      params.delete('region');
      params.delete('isLegacyToBoltSso');
      const search = params.toString();
      // Use React Router's navigate to keep router state in sync
      navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
    }
  }, [location.search]);

  useEffect(() => {
    if (!authenticated) {
      authenticate();
      return;
    }

    initialize(setLoading);
  }, [authenticated]);

  return (
    <div className={`app ${theme.palette.mode}`}>
      {isCheckingAuth ? (
        // BFF Pattern: Show blank shell while verifying auth_token cookie via heartbeat
        <ComponentWithGlobalInit className="container-fluid p-0 vw-100 vh-100 d-flex" />
      ) : (
        <ComponentWithGlobalInit
          className="container-fluid p-0 vw-100 vh-100 d-flex"
          data={{ info, localizations, preferences, settings }}
          shouldCompleteProcess={(!loading && (!authenticated || isLoginPage)) || hasCriticalError500}
          shouldWait={!isLoginPage}
        >
          <ConfirmationSnackbarProvider>
            {authenticated && !isMobile && !isLoginPage && !isInMaintenance && !hasCriticalError500 && (
              <>
                <NavigationMenu />
                <SettingsMenu onLogout={signOut} />
              </>
            )}
            <MainRoutes />
          </ConfirmationSnackbarProvider>
          {!isInMaintenance && <AutoIdleTimer settings={settings} onSignOut={signOut} />}
        </ComponentWithGlobalInit>
      )}
    </div>
  );
};

export default AppContainer;
